<script setup>
import { ref, watch } from 'vue';
import { SectionComponent, MyLoadsCardComponent } from '@/components';
import { useStore } from 'vuex';
import { computed } from 'vue';
import { useScreenSize } from '@/composables/useScreenSize';

const { screenSize } = useScreenSize();
const store = useStore();

const unassignedLoadsSliced = computed(() =>
  store.state.myLoadsStore.unassigned.loads.length > 4
    ? store.state.myLoadsStore.unassigned.loads.slice(0, 4)
    : store.state.myLoadsStore.unassigned.loads
);
const unassignedLoadsCount = computed(
  () => store.state.myLoadsStore.unassigned.pagination.totalRecords
);
const isLoading = computed(() => store.state.myLoadsStore.unassigned.isLoading);
const isDesktop = computed(() => screenSize.value === 'desktop');
const isMobile = computed(() => screenSize.value === 'mobile');

const showContent = ref(true);

defineEmits(['showAssignVehicleModal']);

watch(
  isMobile,
  (newVal) => {
    if (newVal) {
      showContent.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <SectionComponent
    custom-height="h-full"
    custom-width="w-full"
    :custom-padding="isMobile ? 'p-0' : 'p-4'"
    custom-gap="gap-4"
    title="My Loads"
    :sub-title="
      unassignedLoadsSliced.length > 1
        ? isMobile
          ? `${unassignedLoadsCount} Action Needed Loads`
          : `1 - ${unassignedLoadsSliced.length} of ${unassignedLoadsCount} Action Needed Loads`
        : `${unassignedLoadsSliced.length === 1 ? '1 Action Needed Load' : '0 Action Needed Loads'}`
    "
    :is-accordion="isMobile"
    :show-content="showContent"
    @header-click="
      () => {
        if (!isMobile) return;
        showContent = !showContent;
      }
    "
  >
    <template #rightHeader>
      <RouterLink
        v-if="!isMobile"
        to="/carrier/loads"
        class="custom-button default-button flex h-11 w-fit items-center justify-center gap-4 rounded-[5px] text-sm outline outline-1 outline-offset-[-1px] outline-steel-40"
      >
        View All
      </RouterLink>
      <div
        v-else
        class="flex items-center gap-4"
      >
        <img
          v-if="unassignedLoadsSliced.length"
          src="@/assets/alert-square-red.svg"
          class="flex items-center gap-4"
        />
        <img
          src="@/assets/chevron-right-dark-gray.svg"
          class="w-6"
          :class="showContent ? '-rotate-90 transform' : ''"
          alt="chevron-right"
        />
      </div>
    </template>
    <template #content>
      <transition
        enter-active-class="transition duration-25 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div v-if="showContent">
          <div
            v-if="isLoading && !unassignedLoadsSliced.length"
            class="container-max overflow-hidden"
          >
            <div class="mx-auto h-16 w-16 text-center">
              <img
                class="loading relative w-full"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading"
              />
            </div>
          </div>
          <div
            v-else-if="unassignedLoadsSliced.length"
            class="flex items-center pb-4"
            :class="[
              isMobile
                ? 'w-full flex-wrap justify-center gap-4 px-4'
                : '-mx-4 min-w-0 max-w-full flex-1 flex-nowrap justify-start gap-4 overflow-x-auto px-4',
            ]"
          >
            <MyLoadsCardComponent
              v-for="load in unassignedLoadsSliced"
              :key="load.id"
              :load="load"
              class="flex-shrink-0"
              @show-assign-vehicle-modal="
                (load, origin, destination) =>
                  $emit('showAssignVehicleModal', load, origin, destination)
              "
            />
            <RouterLink
              v-if="isMobile"
              to="/carrier/loads"
              class="custom-button default-button flex h-11 w-full items-center justify-center gap-4 rounded-[5px] text-sm outline outline-1 outline-offset-[-1px] outline-steel-40"
            >
              View All
            </RouterLink>
          </div>
          <div
            v-else
            class="flex h-full w-full justify-center"
          >
            <div
              class="flex h-full w-full max-w-[1400px] flex-col items-center justify-center gap-4 self-stretch overflow-hidden bg-white px-4 py-6"
            >
              <img
                src="@/assets/trailer-3-wheel.svg"
                class="w-[108px]"
              />
              <div
                class="flex flex-col items-center justify-start gap-4 self-stretch"
              >
                <div
                  class="w-3/4 text-center font-medium leading-7 text-steel-900"
                  :class="isDesktop ? 'text-lg' : 'text-base'"
                >
                  {{
                    `You don’t have loads that need your attention right now.`
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </SectionComponent>
</template>

<style lang="scss" scoped></style>
